/**
 * SUBSCRIPTION POOL MANAGER
 *
 * Manages Firebase subscriptions with reference counting to prevent duplicate callbacks.
 *
 * Key Features:
 * - One subscription per unique document ID (across entire app)
 * - Reference counting (subscribe when refs=1, unsubscribe when refs=0)
 * - Automatic lifecycle management
 * - Comprehensive logging
 *
 * Architecture:
 * - Component A needs doc X → refs[X] = 1, subscribe to X
 * - Component B needs doc X → refs[X] = 2, reuse existing subscription
 * - Component A unmounts → refs[X] = 1, keep subscription
 * - Component B unmounts → refs[X] = 0, unsubscribe from X
 */

import { type Unsubscribe } from "firebase/firestore"

import Firebase from "#classes/Firebase"
import {
  type DOC_TYPES,
  type FirebaseChange,
} from "#customTypes/composedTypes/firebaseTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { type DataWithId } from "#customTypes/types"
import { reportFirebaseSubscriptionTime } from "#hooks/jotai/useSubscription"

interface SubscriptionInfo {
  refCount: number
  unsubscribe: Unsubscribe
  subscribedAt: number
}

/**
 * Subscription Pool Class
 * Generic pool that works for any document type
 */
class SubscriptionPool<T extends DataWithId> {
  private subscriptions = new Map<Uuid, SubscriptionInfo>()
  private batchSubscriptions = new Map<string, SubscriptionInfo>()
  // private logPrefix: string
  private docType: DOC_TYPES
  private updateDocsCallback: (
    updates: Array<{ id: Uuid; doc: T | null }>
  ) => void

  // Batching mechanism: collect updates and flush on next microtask
  private pendingUpdates = new Map<Uuid, T | null>()
  private flushScheduled = false
  // private batchStartTime: number | null = null

  constructor(
    docType: DOC_TYPES,
    updateDocsCallback: (updates: Array<{ id: Uuid; doc: T | null }>) => void
  ) {
    this.docType = docType
    this.updateDocsCallback = updateDocsCallback
    // this.logPrefix = `[Pool:${docType}]`
    // console.log(`${this.logPrefix} 🏊 Pool initialized`)
  }

  /**
   * Queue an update to be flushed on the next microtask
   * This batches multiple synchronous updates together
   *
   * WHY THIS MATTERS:
   * When Firebase sends multiple document changes, each individual listener
   * fires its callback separately. Without batching, this would cause:
   * 1. Multiple Jotai atom updates (one per document)
   * 2. Multiple component re-renders (even with React 18 batching)
   * 3. Slower UI updates
   *
   * By queueing updates and flushing on the next microtask:
   * - All updates that fire in the same tick are collected
   * - They're sent to Jotai as a single batch
   * - Components re-render once with all changes
   * - Much faster and more efficient!
   */
  private queueUpdate(id: Uuid, doc: T | null): void {
    // const isFirstInBatch = this.pendingUpdates.size === 0

    // if (isFirstInBatch) {
    //   this.batchStartTime = performance.now()
    //   // console.log(
    //   //   `${this.logPrefix} ⏱️ Batch started at ${this.batchStartTime.toFixed(2)}ms`
    //   // )
    // }

    this.pendingUpdates.set(id, doc)

    if (!this.flushScheduled) {
      this.flushScheduled = true
      // Use queueMicrotask to batch updates that happen in the same tick
      queueMicrotask(() => {
        this.flushUpdates()
      })
    }
  }

  /**
   * Flush all pending updates at once
   * If specificIds is provided, only flush updates for those IDs
   */
  private flushUpdates(specificIds?: Uuid[]): void {
    if (this.pendingUpdates.size === 0) {
      this.flushScheduled = false
      return
    }

    let updates: Array<{ id: Uuid; doc: T | null }>

    if (specificIds) {
      // Flush only updates for the specified IDs
      updates = specificIds
        .filter((id) => this.pendingUpdates.has(id))
        .map((id) => ({
          id,
          doc: this.pendingUpdates.get(id)!,
        }))
      // Remove only the flushed IDs from pending
      specificIds.forEach((id) => this.pendingUpdates.delete(id))
    } else {
      // Flush all pending updates
      updates = Array.from(this.pendingUpdates.entries()).map(([id, doc]) => ({
        id,
        doc,
      }))
      this.pendingUpdates.clear()
    }

    if (updates.length === 0) {
      this.flushScheduled = false
      return
    }

    // const flushStartTime = performance.now()
    // const batchCollectionTime = this.batchStartTime
    //   ? flushStartTime - this.batchStartTime
    //   : 0

    // console.log(
    //   `${this.logPrefix} 🚀 Flushing ${updates.length} batched updates at ${flushStartTime.toFixed(2)}ms (collected over ${batchCollectionTime.toFixed(2)}ms)`
    // )

    // const updateStartTime = performance.now()
    this.updateDocsCallback(updates)
    // const updateDuration = performance.now() - updateStartTime

    // console.log(
    //   `${this.logPrefix} ✅ Batch update completed in ${updateDuration.toFixed(2)}ms (total batch time: ${(performance.now() - (this.batchStartTime ?? 0)).toFixed(2)}ms)`
    // )

    // If we flushed specific IDs and there are still pending updates, reschedule flush
    if (specificIds && this.pendingUpdates.size > 0) {
      if (!this.flushScheduled) {
        this.flushScheduled = true
        queueMicrotask(() => {
          this.flushUpdates()
        })
      }
    } else {
      this.flushScheduled = false
    }
    // this.batchStartTime = null
  }

  /**
   * Register interest in a document ID
   * Creates subscription if this is the first component interested
   */
  registerIds(ids: Uuid[]): void {
    // console.log(`${this.logPrefix} 📝 Registering ${ids.length} IDs`)

    ids.forEach((id) => {
      const existing = this.subscriptions.get(id)

      if (existing) {
        // Already subscribed, just increment ref count
        existing.refCount++
        // console.log(
        //   `${this.logPrefix} ➕ ID ${id} ref count: ${existing.refCount - 1} → ${existing.refCount}`
        // )
      } else {
        // First component needs this ID, create subscription
        // console.log(
        //   `${this.logPrefix} 🔌 Creating NEW subscription for ID: ${id}`
        // )

        const firebaseStart = performance.now()
        const unsubscribe = Firebase.getInstance().createListenerForId<T>(
          this.docType,
          id,
          (data: T | null) => {
            // console.log(
            //   `${this.logPrefix} 📥 Received update for ${id}:`,
            //   data ? `${(data as any).firstName || "data"}` : "null"
            // )

            // Queue update for batching (will flush on next microtask)
            this.queueUpdate(id, data)
          }
        )
        const firebaseTime = performance.now() - firebaseStart
        reportFirebaseSubscriptionTime(firebaseTime)

        this.subscriptions.set(id, {
          refCount: 1,
          unsubscribe,
          subscribedAt: Date.now(),
        })

        // console.log(
        //   `${this.logPrefix} ✅ Subscription created for ${id}, ref count: 1`
        // )
      }
    })

    this.logPoolState()
  }

  /**
   * Unregister interest in a document ID
   * Removes subscription if no components are interested anymore
   */
  unregisterIds(ids: Uuid[]): void {
    // console.log(`${this.logPrefix} 📝 Unregistering ${ids.length} IDs`)

    // First, flush any pending updates for these IDs before unsubscribing
    // This prevents losing updates that were queued but not yet flushed
    const idsToUnsubscribe: Uuid[] = []

    ids.forEach((id) => {
      const existing = this.subscriptions.get(id)

      if (!existing) {
        // console.warn(
        //   `${this.logPrefix} ⚠️ Attempted to unregister ${id} but it's not in pool`
        // )
        return
      }

      existing.refCount--
      // console.log(
      //   `${this.logPrefix} ➖ ID ${id} ref count: ${existing.refCount + 1} → ${existing.refCount}`
      // )

      if (existing.refCount <= 0) {
        idsToUnsubscribe.push(id)
      }
    })

    // Flush pending updates for IDs that will be unsubscribed
    // Do this BEFORE unsubscribing to ensure no updates are lost
    if (idsToUnsubscribe.length > 0) {
      // Force immediate flush of updates for these IDs (synchronously)
      // This ensures any queued updates are applied before we unsubscribe
      this.flushUpdates(idsToUnsubscribe)

      // Now unsubscribe
      idsToUnsubscribe.forEach((id) => {
        const existing = this.subscriptions.get(id)
        if (existing) {
          // console.log(
          //   `${this.logPrefix} 🔌 Unsubscribing from ${id} (ref count = 0)`
          // )
          existing.unsubscribe()
          this.subscriptions.delete(id)
          // console.log(`${this.logPrefix} ✅ Subscription removed for ${id}`)
        }
      })
    }

    this.logPoolState()
  }

  /**
   * Register batch subscription (for useSomeDocs)
   * Uses Firebase's "in" query for efficiency
   */
  registerBatch(ids: Uuid[], batchKey: string): void {
    if (ids.length === 0) return

    const existing = this.batchSubscriptions.get(batchKey)

    if (existing) {
      existing.refCount++
      // console.log(
      //   `${this.logPrefix} ➕ Batch ${batchKey.slice(0, 30)} ref count: ${existing.refCount - 1} → ${existing.refCount}`
      // )
      return
    }

    // console.log(
    //   `${this.logPrefix} 🔌 Creating NEW batch subscription for ${ids.length} IDs`
    // )

    // Firebase limits "in" queries to 30 IDs
    const BATCH_SIZE = 30
    const batches: Uuid[][] = []
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE))
    }

    const unsubscribers = batches.map((batch) => {
      // console.log(
      //   `${this.logPrefix} 🔌 Subscribing to batch ${batchIndex + 1}/${batches.length} (${batch.length} IDs)`
      // )

      const firebaseStart = performance.now()
      const unsubscribe = Firebase.getInstance().createListenerForIds<T>(
        this.docType,
        batch,
        (changes: FirebaseChange<T>[]) => {
          // console.log(
          //   `${this.logPrefix} 📥 Batch ${batchIndex + 1} received ${changes.length} changes`
          // )

          // Queue all updates for batching
          changes.forEach(({ doc, type }) => {
            this.queueUpdate(doc.id, type === "removed" ? null : doc)
          })
        }
      )
      const firebaseTime = performance.now() - firebaseStart
      reportFirebaseSubscriptionTime(firebaseTime)
      return unsubscribe
    })
    // Combined unsubscribe function
    const unsubscribe = () => {
      unsubscribers.forEach((unsub) => unsub())
    }

    this.batchSubscriptions.set(batchKey, {
      refCount: 1,
      unsubscribe,
      subscribedAt: Date.now(),
    })

    // console.log(`${this.logPrefix} ✅ Batch subscription created, ref count: 1`)
    this.logPoolState()
  }

  /**
   * Unregister batch subscription
   */
  unregisterBatch(batchKey: string): void {
    const existing = this.batchSubscriptions.get(batchKey)

    if (!existing) {
      // console.warn(
      //   `${this.logPrefix} ⚠️ Attempted to unregister batch ${batchKey.slice(0, 30)} but it's not in pool`
      // )
      return
    }

    existing.refCount--
    // console.log(
    //   `${this.logPrefix} ➖ Batch ${batchKey.slice(0, 30)} ref count: ${existing.refCount + 1} → ${existing.refCount}`
    // )

    if (existing.refCount <= 0) {
      // Flush all pending updates before unsubscribing batch
      // (We can't filter by specific IDs for batches, so flush all)
      this.flushUpdates()

      // console.log(
      //   `${this.logPrefix} 🔌 Unsubscribing from batch ${batchKey.slice(0, 30)} (ref count = 0)`
      // )
      existing.unsubscribe()
      this.batchSubscriptions.delete(batchKey)
      // console.log(`${this.logPrefix} ✅ Batch subscription removed`)
    }

    this.logPoolState()
  }

  /**
   * Log current pool state for debugging
   */
  private logPoolState(): void {
    // const individualCount = this.subscriptions.size
    // const batchCount = this.batchSubscriptions.size
    // const totalRefCount = Array.from(this.subscriptions.values()).reduce(
    //   (sum, sub) => sum + sub.refCount,
    //   0
    // )
    // console.log(
    //   `${this.logPrefix} 📊 Pool state: ${individualCount} individual subs (${totalRefCount} total refs), ${batchCount} batch subs`
    // )
  }

  /**
   * Get pool stats for debugging
   */
  getStats() {
    return {
      individualSubscriptions: this.subscriptions.size,
      batchSubscriptions: this.batchSubscriptions.size,
      totalRefs: Array.from(this.subscriptions.values()).reduce(
        (sum, sub) => sum + sub.refCount,
        0
      ),
      details: {
        individual: Array.from(this.subscriptions.entries()).map(
          ([id, info]) => ({
            id: id,
            refCount: info.refCount,
            ageMs: Date.now() - info.subscribedAt,
          })
        ),
        batch: Array.from(this.batchSubscriptions.entries()).map(
          ([key, info]) => ({
            key: key.slice(0, 30),
            refCount: info.refCount,
            ageMs: Date.now() - info.subscribedAt,
          })
        ),
      },
    }
  }
}

// Singleton pools for each doc type
const pools = new Map<DOC_TYPES, SubscriptionPool<any>>()

/**
 * Get or create pool for a doc type
 */
export function getSubscriptionPool<T extends DataWithId>(
  docType: DOC_TYPES,
  updateDocsCallback: (updates: Array<{ id: Uuid; doc: T | null }>) => void
): SubscriptionPool<T> {
  if (!pools.has(docType)) {
    pools.set(docType, new SubscriptionPool<T>(docType, updateDocsCallback))
  }
  return pools.get(docType) as SubscriptionPool<T>
}

/**
 * Get all pool stats (for debugging)
 */
export function getAllPoolStats() {
  const stats: Record<string, any> = {}
  pools.forEach((pool, docType) => {
    stats[docType] = pool.getStats()
  })
  return stats
}
